/**
 * useImageEditor Hook
 *
 * Main state management for Vertex AI Image Editor
 * Manages task selection, image, mask, and generation state
 */

import { useState, useCallback } from 'react';
import type { Image } from '@/types';

export type EditTask = 'inpaint_add' | 'inpaint_remove' | 'outpaint' | 'background_edit';

interface UseImageEditorReturn {
  selectedTask: EditTask;
  setSelectedTask: (task: EditTask) => void;
  originalImage: File | null;
  setOriginalImage: (file: File | null) => void;
  preview: string | null;
  mask: Blob | null;
  setMask: (mask: Blob | null) => void;
  generatedImages: Image[];
  setGeneratedImages: (images: Image[]) => void;
  isGenerating: boolean;
  setIsGenerating: (generating: boolean) => void;
  targetAspectRatio: string;
  setTargetAspectRatio: (ratio: string) => void;
  clearAll: () => void;
}

export function useImageEditor(): UseImageEditorReturn {
  const [selectedTask, setSelectedTask] = useState<EditTask>('inpaint_add');
  const [originalImage, setOriginalImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [mask, setMask] = useState<Blob | null>(null);
  const [generatedImages, setGeneratedImages] = useState<Image[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [targetAspectRatio, setTargetAspectRatio] = useState<string>('1:1');

  const handleSetOriginalImage = useCallback((file: File | null) => {
    console.log('🖼️ Setting original image:', file);
    setOriginalImage(file);
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      console.log('🔗 Preview URL created:', previewUrl);
      setPreview(previewUrl);
    } else {
      setPreview(null);
    }
  }, []);

  const clearAll = useCallback(() => {
    setOriginalImage(null);
    setPreview(null);
    setMask(null);
    setGeneratedImages([]);
    setIsGenerating(false);
  }, []);

  return {
    selectedTask,
    setSelectedTask,
    originalImage,
    setOriginalImage: handleSetOriginalImage,
    preview,
    mask,
    setMask,
    generatedImages,
    setGeneratedImages,
    isGenerating,
    setIsGenerating,
    targetAspectRatio,
    setTargetAspectRatio,
    clearAll,
  };
}
